/**
 * workOrder (SUB-SEND-02 + SUB-AUTO-RESEND) — the install work order the
 * subcontractor gets, built from the job record in ONE place: the job page's
 * Send / Preview, the warehouse's Print, and the "the install moved — send the
 * new date?" prompt all load, build and send through here, so the sub can never
 * get two different versions of the same job.
 *
 * No pricing, ever. Sends ride send-email (comms-gated: HELD while comms_live
 * is off; a TEST job is refused and recorded as "test").
 */
import { supabase } from "@/lib/supabase";
import { resolveNames, type Job, type JobVisit } from "./jobs";
import { loadEquipFacts, specGroups, specLines, type InstallBank, type SpecLine } from "./installSpec";

export const WO_LOGO = "https://trfusfityhwecvglnfhm.supabase.co/storage/v1/object/public/email-assets/sedgwick-logo.png";

export const fmtDate = (s: string | null | undefined) =>
  !s ? "" : new Date(s.length <= 10 ? s + "T00:00:00" : s).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
const shortDay = (s: string) => new Date(`${s}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
export const dayText = (d: { date: string; window: string | null } | null) => (d ? `${fmtDate(d.date)}${d.window ? ` · ${d.window}` : ""}` : "");

/** PERMIT-01: " (requested)" / " (builder's)" so a packet never reads a
 *  not-yet-pulled or builder-pulled permit as ours in hand. */
export function permitTag(p: { status: string; by_builder?: boolean | null }): string {
  if (p.by_builder) return " (builder's)";
  if (p.status === "requested") return " (requested)";
  return "";
}

/** The job address with its city once — the address often already ends with it. */
export function jobAddressLine(job: Pick<Job, "address" | "service_city">): string {
  const a = (job.address ?? "").trim();
  const c = (job.service_city ?? "").trim();
  if (!c || a.toLowerCase().includes(c.toLowerCase())) return a;
  return [a, c].filter(Boolean).join(", ");
}

export function esc(s: string | null | undefined): string {
  return (s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** The job's install days, first first — every dated install visit (a
 *  two-day install lists both), else any dated visit. */
export function installDaysOf(visits: Pick<JobVisit, "scheduled_date" | "scheduled_window" | "assigned_empno" | "visit_role">[]): { date: string; window: string | null; empno: string | null }[] {
  const dated = visits.filter((v) => !!v.scheduled_date);
  const inst = dated.filter((v) => v.visit_role === "install");
  return (inst.length ? inst : dated)
    .map((v) => ({ date: v.scheduled_date as string, window: v.scheduled_window, empno: v.assigned_empno }))
    .sort((a, b) => a.date.localeCompare(b.date));
}
/** The first install day (what the sub shows up for), or null = not scheduled yet. */
export function firstInstallDay(visits: Pick<JobVisit, "scheduled_date" | "scheduled_window" | "assigned_empno" | "visit_role">[]) {
  return installDaysOf(visits)[0] ?? null;
}

export interface WorkOrderDoc {
  job: Job;
  installDays: { date: string; window: string | null }[];
  installerName: string | null;
  equipment: { category: string | null; manufacturer: string | null; model: string | null; part_number: string | null; qty: number }[];
  permits: { trade: string | null; permit_no: string | null; jurisdiction: string | null }[];
  access: { label: string; value: string }[];
  contact: { phone1: string | null; phone2: string | null; email: string | null };
  details: SpecLine[];
  /** The question bank the details came from (section titles + order). */
  bank?: InstallBank;
}

/** The work order document (customer-facing chrome, NO pricing). `movedFrom`
 *  = the day the sub was told before — prints an UPDATED banner on top;
 *  `backOn` = they were last told it's postponed — prints BACK ON THE SCHEDULE. */
export function buildWorkOrderHtml(o: WorkOrderDoc, movedFrom?: { date: string; window: string | null } | null, backOn = false): string {
  const { job, installDays, installerName, equipment, permits, access, contact, details } = o;
  const cell = "padding:5px 12px 5px 0;vertical-align:top";
  const specBlocks = specGroups(details, o.bank).map((g) => `
    <div style="font-size:12px;font-weight:700;color:#16314f;margin:10px 0 2px">${esc(g.title)}</div>
    <table style="border-collapse:collapse;width:100%">${g.lines.map((r) =>
      `<tr><td style="${cell};color:#5f6b7a;font-size:13px;white-space:nowrap;width:200px">${esc(r.label)}</td><td style="${cell};color:#16314f;font-size:14px;font-weight:600">${esc(r.value)}</td></tr>`).join("")}</table>`).join("");
  const row = (l: string, v: string) =>
    `<tr><td style="${cell};color:#5f6b7a;font-size:13px;white-space:nowrap">${l}</td><td style="${cell};color:#16314f;font-size:14px;font-weight:600">${v}</td></tr>`;
  const equipRows = equipment.map((e) =>
    `<tr>
      <td style="padding:7px 8px;border-bottom:1px solid #eef0f3;color:#5f6b7a;font-size:13px">${esc(e.category) || "—"}</td>
      <td style="padding:7px 8px;border-bottom:1px solid #eef0f3;color:#16314f;font-size:14px;font-weight:700">${esc([e.manufacturer, e.model].filter(Boolean).join(" "))}</td>
      <td style="padding:7px 8px;border-bottom:1px solid #eef0f3;color:#5f6b7a;font-size:13px">${esc(e.part_number) || ""}</td>
      <td style="padding:7px 8px;border-bottom:1px solid #eef0f3;color:#16314f;font-size:14px;text-align:right">${e.qty || 1}</td>
    </tr>`).join("");
  const accessRows = access.map((a) =>
    `<tr><td style="padding:5px 0;color:#16314f;font-size:14px;border-bottom:1px solid #eef0f3"><strong>${esc(a.label)}:</strong> ${esc(a.value)}</td></tr>`).join("");
  const permitLine = permits.length
    ? permits.map((p) => `${esc(p.trade) || "Permit"}${p.permit_no ? ` #${esc(p.permit_no)}` : ""}${p.jurisdiction ? ` · ${esc(p.jurisdiction)}` : ""}`).join("<br/>")
    : "No permits on this job.";
  const dateLine = installDays.length === 0
    ? `<span style="color:#b45309">Not scheduled yet</span>`
    : installDays.map((d) => esc(dayText(d))).join("<br/>");
  const head = (t: string) => `<div style="font-size:12px;text-transform:uppercase;letter-spacing:.05em;color:#5f6b7a;font-weight:700;margin:0 0 6px">${t}</div>`;
  const banner = movedFrom && installDays[0]
    ? `<div style="background:#fff7ed;border:1px solid #fed7aa;border-radius:10px;padding:10px 14px;margin:0 0 16px;color:#9a3412;font-size:14px;font-weight:700">UPDATED — the install moved. Was ${esc(dayText(movedFrom))}; now ${esc(dayText(installDays[0]))}.</div>`
    : backOn && installDays[0]
      ? `<div style="background:#ecfdf5;border:1px solid #a7f3d0;border-radius:10px;padding:10px 14px;margin:0 0 16px;color:#065f46;font-size:14px;font-weight:700">BACK ON THE SCHEDULE — this install was postponed. It's now ${esc(dayText(installDays[0]))}.</div>`
      : "";
  return `<div style="max-width:680px;margin:0 auto;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;background:#ffffff;border:1px solid #e8eaed;border-radius:14px;overflow:hidden">
  <div style="display:flex;align-items:center;justify-content:space-between;padding:22px 28px;border-bottom:3px solid #16314f">
    <img src="${WO_LOGO}" alt="Sedgwick Heating" style="width:170px;max-width:50%"/>
    <div style="text-align:right">
      <div style="font-size:16px;font-weight:800;color:#16314f">INSTALLATION WORK ORDER</div>
      <div style="font-size:12px;color:#5f6b7a">Ref ${esc(job.id.slice(0, 8))} · ${new Date().toLocaleDateString("en-US")}</div>
    </div>
  </div>
  <div style="padding:24px 28px">
    ${banner}
    <div style="font-size:18px;font-weight:800;color:#16314f;margin:0 0 12px">${esc(job.title) || "System installation"}</div>
    <table style="border-collapse:collapse;margin:0 0 18px">
      ${row(installDays.length > 1 ? "Install days" : "Install date", dateLine)}
      ${installerName ? row("Installer", esc(installerName)) : ""}
      ${row("Customer", esc(job.customer_name) || esc(job.custno))}
      ${row("Job address", esc(jobAddressLine(job)) || "—")}
      ${contact.phone1 ? row("Phone", esc(contact.phone1)) : ""}
      ${contact.phone2 ? row("Phone 2", esc(contact.phone2)) : ""}
      ${row("Permits", permitLine)}
    </table>
    ${head("Equipment &amp; accessories")}
    <table style="width:100%;border-collapse:collapse;margin:0 0 18px">
      <tr>
        <td style="padding:6px 8px;background:#f6f8fb;color:#5f6b7a;font-size:11px;text-transform:uppercase;letter-spacing:.05em;font-weight:700">Type</td>
        <td style="padding:6px 8px;background:#f6f8fb;color:#5f6b7a;font-size:11px;text-transform:uppercase;letter-spacing:.05em;font-weight:700">Unit</td>
        <td style="padding:6px 8px;background:#f6f8fb;color:#5f6b7a;font-size:11px;text-transform:uppercase;letter-spacing:.05em;font-weight:700">Part #</td>
        <td style="padding:6px 8px;background:#f6f8fb;color:#5f6b7a;font-size:11px;text-transform:uppercase;letter-spacing:.05em;font-weight:700;text-align:right">Qty</td>
      </tr>
      ${equipRows || `<tr><td colspan="4" style="padding:10px 8px;color:#5f6b7a;font-size:13px">No equipment on the job yet.</td></tr>`}
    </table>
    ${specBlocks ? `${head("Install details")}<div style="margin:0 0 18px">${specBlocks}</div>` : ""}
    ${accessRows ? `${head("Access &amp; site notes")}<table style="width:100%;border-collapse:collapse;margin:0 0 18px">${accessRows}</table>` : ""}
    <p style="color:#5f6b7a;font-size:13px;line-height:1.6;margin:0">Questions? Call the Sedgwick office at <a href="tel:9528819000" style="color:#1f5fa8;font-weight:700">(952) 881-9000</a>. This work order contains no customer pricing.</p>
  </div>
  <div style="text-align:center;font-size:12px;color:#5f6b7a;padding:0 0 18px">Sedgwick Heating &amp; Air Conditioning · Eagan, MN</div>
</div>`;
}

/** The short "postponed" notice — the install lost its date after the sub got it. */
function buildPostponedHtml(job: Job, was: { date: string; window: string | null }): string {
  return `<div style="max-width:620px;margin:0 auto;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;background:#ffffff;border:1px solid #e8eaed;border-radius:14px;overflow:hidden">
  <div style="display:flex;align-items:center;justify-content:space-between;padding:22px 28px;border-bottom:3px solid #16314f">
    <img src="${WO_LOGO}" alt="Sedgwick Heating" style="width:170px;max-width:50%"/>
    <div style="font-size:16px;font-weight:800;color:#b45309">INSTALL POSTPONED</div>
  </div>
  <div style="padding:24px 28px;color:#16314f;font-size:15px;line-height:1.6">
    <p style="margin:0 0 12px"><strong>${esc(job.customer_name) || esc(job.custno)}</strong> — ${esc(jobAddressLine(job)) || "the job address"}</p>
    <p style="margin:0 0 12px">The install scheduled for <strong>${esc(dayText(was))}</strong> is postponed — please don't come out that day. We'll send the new date as soon as it's set.</p>
    <p style="color:#5f6b7a;font-size:13px;margin:0">Questions? Call the Sedgwick office at <a href="tel:9528819000" style="color:#1f5fa8;font-weight:700">(952) 881-9000</a>.</p>
  </div>
</div>`;
}

/** Open a document in a new window (preview, or print). The window opens on
 *  the click (so a popup blocker allows it) and fills when the document is ready. */
export function openWorkOrder(html: string | (() => Promise<string>), print: boolean) {
  const w = window.open("", "_blank");
  if (!w) return;
  const page = (body: string) => `<!doctype html><html><head><meta charset="utf-8"><title>Install Work Order</title></head><body style="margin:24px;background:#f6f8fb">${body}</body></html>`;
  const fill = (h: string) => {
    w.document.open();
    w.document.write(page(h));
    w.document.close();
    w.focus();
    if (print) setTimeout(() => w.print(), 250);
  };
  if (typeof html === "string") { fill(html); return; }
  w.document.write(page(`<p style="font-family:Helvetica,Arial,sans-serif;color:#5f6b7a">Building the work order…</p>`));
  html().then(fill, () => fill(`<p style="font-family:Helvetica,Arial,sans-serif;color:#b91c1c">Couldn't build the work order — close this and try again.</p>`));
}

export interface SubLite { id: string; name: string; email: string | null; active: boolean }
export interface WorkOrderPacket extends WorkOrderDoc {
  days: { date: string; window: string | null; empno: string | null }[];
  first: { date: string; window: string | null; empno: string | null } | null;
  subs: SubLite[];
  lastSend: { to_name: string | null; status: string; kind: string | null; install_date: string | null; install_window: string | null } | null;
}

/** Everything the work order needs, straight from the database. */
export async function loadWorkOrder(jobId: string, memberName: (empno: string | null | undefined) => string | null, bank?: InstallBank): Promise<WorkOrderPacket> {
  const { data: jobRow, error } = await supabase.from("pegasus_job").select("*").eq("id", jobId).maybeSingle();
  if (error || !jobRow) throw new Error("job_not_found");
  const [job] = await resolveNames([jobRow as Job]);
  const [{ data: visits }, { data: equip }, { data: permits }, { data: signals }, { data: locs }, { data: subs }, { data: sends }] = await Promise.all([
    supabase.from("pegasus_dispatch").select("id, visit_role, scheduled_date, scheduled_window, assigned_empno")
      .eq("job_id", jobId).is("deleted_at", null),
    supabase.from("pegasus_job_equipment").select("category, manufacturer, model, part_number, qty, equip_key, kind, item_id")
      .eq("job_id", jobId).order("created_at").order("id"),
    supabase.from("pegasus_permit").select("trade, permit_no, jurisdiction, status, by_builder").eq("job_id", jobId),
    job.custno ? supabase.from("extracted_signal").select("signal_type, value, status, archived_at").eq("custno", job.custno) : Promise.resolve({ data: [] }),
    job.custno ? supabase.from("mirror_location").select("phone1, phone2, email").eq("custno", job.custno).order("locno").limit(1) : Promise.resolve({ data: [] }),
    supabase.from("pegasus_subcontractor").select("id, name, email, active").order("name"),
    supabase.from("pegasus_job_send").select("to_name, status, kind, install_date, install_window").eq("job_id", jobId).order("sent_at", { ascending: false }).limit(1),
  ]);
  const vs = (visits ?? []) as Pick<JobVisit, "scheduled_date" | "scheduled_window" | "assigned_empno" | "visit_role">[];
  const days = installDaysOf(vs);
  const first = days[0] ?? null;
  const units = (equip ?? []) as { category: string | null; manufacturer: string | null; model: string | null; part_number: string | null; qty: number; equip_key: string | null; kind: string | null; item_id: string | null }[];
  const facts = await loadEquipFacts(units);
  const loc = ((locs ?? []) as { phone1: string | null; phone2: string | null; email: string | null }[])[0];
  return {
    job,
    days,
    first,
    installDays: days,
    installerName: first?.empno ? memberName(first.empno) : null,
    equipment: units.map((e) => ({ category: e.category, manufacturer: e.manufacturer, model: e.model, part_number: e.part_number, qty: e.qty || 1 })),
    permits: ((permits ?? []) as { trade: string | null; permit_no: string | null; jurisdiction: string | null; status: string; by_builder: boolean | null }[])
      .filter((p) => p.status !== "cancelled")
      .map((p) => ({ trade: `${p.trade ?? "Permit"}${permitTag(p)}`, permit_no: p.permit_no, jurisdiction: p.jurisdiction })),
    access: ((signals ?? []) as { signal_type: string; value: string | null; status: string; archived_at: string | null }[])
      .filter((s) => s.status === "confirmed" && !s.archived_at && !!s.value)
      .map((s) => ({ label: s.signal_type.replace(/_/g, " "), value: s.value as string })),
    contact: { phone1: loc?.phone1 ?? null, phone2: loc?.phone2 ?? null, email: loc?.email ?? null },
    details: specLines(job.install_spec, facts, bank),
    bank,
    subs: ((subs ?? []) as SubLite[]).filter((s) => s.active),
    lastSend: ((sends ?? []) as WorkOrderPacket["lastSend"][])[0] ?? null,
  };
}

export type SendOutcome = "sent" | "held" | "test";

/** Send the work order (or a "postponed" notice) and stamp the receipt on the job. */
/** The work order email exactly as the sub gets it — subject, banner and all
 *  (UPDATED when the day moved since the last send, BACK ON THE SCHEDULE after
 *  a postponed notice). The job page's Preview shows this same html. */
export function workOrderEmail(p: WorkOrderPacket): { subject: string; html: string } {
  const job = p.job;
  const who = `${job.customer_name ?? job.custno}${job.address ? ` · ${job.address}` : ""}`;
  const last = p.lastSend;
  const first = p.first;
  const backOn = !!first && !!last && (last.kind ?? "work_order") === "postponed";
  const moved = !!first && !!last && !backOn
    && ((last.install_date ?? null) !== first.date || (last.install_window ?? null) !== (first.window ?? null));
  const movedFrom = moved && last?.install_date ? { date: last.install_date, window: last.install_window } : null;
  const subject = `${backOn ? "RESCHEDULED install work order" : moved ? "UPDATED install work order" : "Install work order"}${first ? ` — ${shortDay(first.date)}` : ""} — ${who}`;
  return { subject, html: buildWorkOrderHtml(p, movedFrom, backOn) };
}

export async function sendToSub(p: WorkOrderPacket, sub: SubLite, kind: "work_order" | "postponed"): Promise<SendOutcome> {
  if (!sub.email) throw new Error("no_email");
  const job = p.job;
  const who = `${job.customer_name ?? job.custno}${job.address ? ` · ${job.address}` : ""}`;
  let subject: string;
  let html: string;
  let installDate: string | null = null;
  let installWindow: string | null = null;
  if (kind === "work_order") {
    if (!p.first) throw new Error("no_date");
    ({ subject, html } = workOrderEmail(p));
    installDate = p.first.date;
    installWindow = p.first.window ?? null;
  } else {
    const was = p.lastSend?.install_date ? { date: p.lastSend.install_date, window: p.lastSend.install_window } : null;
    if (!was) throw new Error("nothing_to_postpone");
    subject = `POSTPONED install — was ${shortDay(was.date)} — ${who}`;
    html = buildPostponedHtml(job, was);
  }
  const { data: res, error } = await supabase.functions.invoke("send-email", {
    body: { to: [sub.email], subject, html, custno: job.custno, message_type: "job-packet", job_id: job.id },
  });
  if (error) throw error;
  const r = (res ?? {}) as { ok?: boolean; blocked?: string };
  const status: SendOutcome = r.ok ? "sent" : r.blocked === "test" || job.is_test ? "test" : "held";
  const { data: u } = await supabase.auth.getUser();
  await supabase.from("pegasus_job_send").insert({
    job_id: job.id, sub_id: sub.id, to_email: sub.email, to_name: sub.name,
    subject, status, kind, sent_by: u?.user?.email ?? null,
    install_date: installDate, install_window: installWindow,
  });
  return status;
}

/** The sub this job's work order goes to — whoever got the last one, else the only / first active sub. */
export function subFor(p: WorkOrderPacket, subId?: string | null): SubLite | null {
  return p.subs.find((s) => s.id === subId)
    ?? p.subs.find((s) => s.name === p.lastSend?.to_name)
    ?? p.subs[0]
    ?? null;
}
